import * as React from "react";
import {
    DismissCircleFilled,
    DismissRegular,
    ChevronDownRegular,
    ChevronRightRegular,
    PlayCircleRegular,
    ClockRegular,
    BranchRegular,
    NumberSymbolSquareRegular,
} from "@fluentui/react-icons";
import { useHealReportViewStyles } from "../styles/healReportView.styles";
import type { ProjectVideoFile, SessionResponse } from "../api/models/ApiModels";
import { findRecordingForVideoPath, mapRecordingsToVideoFiles } from "../utils/recordings";
import { useVideoPlayer } from "../utils/useVideoPlayer";
import { fetchJsonWithRetry } from "../utils/fetchJson";
import { trackException } from "../telemetry";
import { InfoRow, StatusIcon, Section, SummaryStat, AttributionTag } from "./reportShared";

/* ── Heal report JSON schema ── */

interface EvidenceItem {
    area: string;
    signal: string;
    direction: string;
}

interface HealClassification {
    /** Spec file path, exactly as Playwright reports it (relative to rootDir). */
    file: string;
    /** Describe-chain + test title (file excluded). Identity + display source. */
    titlePath?: string[];
    /** New file / title path when a fix moved or renamed the test; null otherwise. */
    afterFile?: string | null;
    afterTitlePath?: string[] | null;
    classification: string;
    action_taken: string;
    /** PR attribution — did this PR cause the failure, or was it pre-existing. */
    origin?: "pr_related" | "pre_existing" | "unknown" | null;
    fix_applied?: string | null;
    developer_action?: string | null;
    evidence?: EvidenceItem[];
    /** Video path set by the enricher post-run; null when the spec couldn't be
     *  resolved, the relevant per-pass JSON was absent, or it produced no video. */
    beforeRecording?: string | null;
    /** Video path for the passing-state recording (fixed tests); null otherwise. */
    afterRecording?: string | null;
}

interface HealReport {
    metadata: {
        timestamp: string;
        branch: string;
        commitSha: string;
        prNumber?: number | null;
        gitRemoteUrl?: string | null;
        commitUrl?: string | null;
        branchUrl?: string | null;
        prUrl?: string | null;
        userInstruction?: string | null;
        /** testDir relative to the repo root, set by the enricher; joined with
         *  `file` to build repo-root links. */
        repoTestRoot?: string | null;
    };
    summary: {
        text: string;
    };
    /** Before-pass execution scope — what actually ran. */
    run?: {
        scoped: boolean;
        specFiles: number;
        tests: number;
        passed: number;
        failed: number;
    };
    classifications: HealClassification[];
}

interface HealReportViewProps {
    session: SessionResponse;
    onClose: () => void;
    projectId?: string;
}

/* ── Helpers ── */

function originLabel(origin?: string | null): string | null {
    if (origin === "pr_related") return "Introduced by this change";
    if (origin === "pre_existing") return "Pre-existing — not introduced by this change";
    return null;
}

/** Group app-defect classifications by their `developer_action` so related findings render together. */
function groupByDeveloperAction(items: HealClassification[]): { action: string; items: HealClassification[] }[] {
    const map = new Map<string, HealClassification[]>();
    for (const cl of items) {
        const key = cl.developer_action?.trim() || "Unknown cause";
        const group = map.get(key) ?? [];
        group.push(cl);
        map.set(key, group);
    }
    return Array.from(map.entries()).map(([action, items]) => ({ action, items }));
}

function testTitle(titlePath?: string[]): string {
    return titlePath?.[titlePath.length - 1] ?? "";
}

function whatWentWrong(cl: HealClassification): string | null {
    if (!cl.evidence?.length) return null;
    const runtime = cl.evidence.find((e) => e.area === "runtime");
    if (runtime) return runtime.signal;
    const codeChange = cl.evidence.find((e) => e.area === "code_change" || e.area === "change_intent");
    return codeChange?.signal ?? null;
}

/** Strip /DefaultCollection from ADO URLs — the collection segment is used in git remotes
 *  but not in browser-facing web URLs. Also normalizes visualstudio.com paths. */
function normalizeAdoUrl(url: string | null | undefined): string | null | undefined {
    if (!url) return url;
    return url.replace(/\/(DefaultCollection)\//i, "/");
}

/** Join the report's `repoTestRoot` prefix with a spec's `file` (which Playwright
 *  reports relative to rootDir) to form the repo-root path used in source links.
 *  Returns null when no prefix is known (file then isn't safely linkable). */
function repoFilePath(repoTestRoot: string | null | undefined, file: string | null | undefined): string | null {
    if (!file || repoTestRoot == null) return null;
    const f = file.replace(/\\/g, "/").replace(/^\/+/, "");
    const prefix = repoTestRoot.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
    return prefix ? `${prefix}/${f}` : f;
}

function buildFileUrl(
    gitRemoteUrl: string | null | undefined,
    commitSha: string | null | undefined,
    repoPath: string | null | undefined
): string | null {
    if (!gitRemoteUrl || !commitSha || !repoPath) return null;
    const remote = normalizeAdoUrl(gitRemoteUrl.replace(/\/$/, ""))!;
    const path = `/${repoPath.replace(/^\/+/, "")}`;
    if (remote.includes("github.com")) {
        return `${remote}/blob/${commitSha}${path}`;
    }
    if (remote.includes("dev.azure.com") || remote.includes("visualstudio.com")) {
        return `${remote}?path=${path}&version=GC${commitSha}`;
    }
    return null;
}

function displayFileName(filePath: string): string {
    return filePath.split("/").pop() ?? filePath;
}

/* ── Recording resolution ──
 *
 * The CLI's heal-report enricher writes each classification's
 * `beforeRecording`/`afterRecording` video path (resolved against
 * before-run.json / after-run.json). The dashboard's job is a single hop:
 *   findRecordingForVideoPath(cl.beforeRecording, videoFiles)
 *
 * Either may be null when the enricher couldn't pair a spec (missing/ambiguous
 * identity, or the relevant per-pass JSON absent) — meaning no video for that side. */

/* ── Component ── */

const HealReportView: React.FC<HealReportViewProps> = ({ session, onClose, projectId }) => {
    const s = useHealReportViewStyles();
    const [report, setReport] = React.useState<HealReport | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const { play, playRecording, modal: videoModal, isPlaying, close: closeVideo } = useVideoPlayer(projectId);

    // Same shape `ProjectDashboard` (run view) uses — single source of truth.
    const videoFiles: ProjectVideoFile[] = React.useMemo(
        () => mapRecordingsToVideoFiles(session.recordings),
        [session]
    );

    const reportSasUrl = React.useMemo(
        () => session.files?.find((f) => f.fileType.trim().toLowerCase() === "heal-report" && !!f.sasUri)?.sasUri ?? null,
        [session]
    );

    React.useEffect(() => {
        if (!reportSasUrl) { setLoading(false); setError("No heal report data available for this session."); return; }
        const ctrl = new AbortController();
        const fetchUrl = import.meta.env.DEV ? `/blob-proxy?url=${encodeURIComponent(reportSasUrl)}` : reportSasUrl;
        fetchJsonWithRetry<HealReport>(fetchUrl, ctrl.signal)
            .then((data) => { setReport(data); setLoading(false); })
            .catch((err) => {
                if (ctrl.signal.aborted) return;
                console.error("[HealReportView] heal-report.json fetch failed", err);
                trackException(err instanceof Error ? err : new Error(String(err)), { source: "HealReportView", action: "fetchHealReport" });
                setError(`Failed to load report: ${err.message}`);
                setLoading(false);
            });
        return () => ctrl.abort();
    }, [reportSasUrl]);

    /** One-hop lookup: read videoPath the CLI enricher wrote, find the
     *  matching uploaded recording. cl.beforeRecording/cl.afterRecording may be null. */
    const resolveBeforeAfter = React.useCallback((cl: HealClassification): { before?: ProjectVideoFile; after?: ProjectVideoFile } => {
        return {
            before: findRecordingForVideoPath(cl.beforeRecording ?? undefined, videoFiles),
            after: findRecordingForVideoPath(cl.afterRecording ?? undefined, videoFiles),
        };
    }, [videoFiles]);

    React.useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (isPlaying) closeVideo();
            else onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose, isPlaying, closeVideo]);

    const timestamp = report?.metadata?.timestamp ?? session.completedDateTime ?? session.createdDateTime;
    const formattedTime = timestamp
        ? new Date(timestamp).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
        : "";

    // A deflaked flaky is `classification:flaky` + `action_taken:fixed` — split it into its own
    // "Stabilized" group so it doesn't render under both Fixed and Flaky.
    // A removed obsolete test carries `action_taken:removed` (any classification) — it's a
    // completed maintenance action, not a live failure, so pull it out of every other bucket
    // (otherwise a removed stale_test falls through to "Needs triage").
    const removed = report?.classifications.filter((c) => c.action_taken === "removed") ?? [];
    const stabilized = report?.classifications.filter((c) => c.action_taken === "fixed" && c.classification === "flaky") ?? [];
    const fixed = report?.classifications.filter((c) => c.action_taken === "fixed" && c.classification !== "flaky") ?? [];
    // `app_defect` is the current classification; `regression` is the legacy token from reports
    // written before the rename — match both so already-published heal reports keep rendering.
    const appDefects = report?.classifications.filter((c) => c.action_taken !== "fixed" && c.action_taken !== "removed" && (c.classification === "app_defect" || c.classification === "regression")) ?? [];
    const flaky = report?.classifications.filter((c) => c.classification === "flaky" && c.action_taken !== "fixed" && c.action_taken !== "removed") ?? [];
    const other = report?.classifications.filter(
        (c) => c.action_taken !== "fixed" && c.action_taken !== "removed" && c.classification !== "app_defect" && c.classification !== "regression" && c.classification !== "flaky"
    ) ?? [];

    const appDefectGroups = groupByDeveloperAction(appDefects);

    const meta = report?.metadata;
    const commitUrl = normalizeAdoUrl(meta?.commitUrl);
    const branchUrl = normalizeAdoUrl(meta?.branchUrl);
    const prUrl = normalizeAdoUrl(meta?.prUrl);

    return (
        <div className={s.overlay}>
            {/* Header */}
            <div className={s.header}>
                <div className={s.headerLeft}>
                    <img src="/icons/app-icon.svg" alt="" className={s.headerLogo} />
                    <span className={s.headerTitleGroup}>
                        <span className={s.headerTitle}>Heal Report</span>
                        <span className={s.badgeHeal}>HEAL</span>
                    </span>
                    {formattedTime && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><ClockRegular fontSize={16} /></span>
                            <span className={s.headerMeta}>{formattedTime}</span>
                        </span>
                    )}
                    {meta?.branch && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><BranchRegular fontSize={16} /></span>
                            {branchUrl
                                ? <a href={branchUrl} target="_blank" rel="noopener noreferrer" className={s.headerMetaLink}>{meta.branch}</a>
                                : <span className={s.headerMeta}>{meta.branch}</span>}
                        </span>
                    )}
                    {meta?.commitSha && (
                        <span className={s.headerMetaItem}>
                            <span className={s.headerMetaIcon}><NumberSymbolSquareRegular fontSize={16} /></span>
                            {commitUrl
                                ? <a href={commitUrl} target="_blank" rel="noopener noreferrer" className={s.headerCommit}>{meta.commitSha.substring(0, 7)}</a>
                                : <span className={s.headerCommit}>{meta.commitSha.substring(0, 7)}</span>}
                        </span>
                    )}
                    {meta?.prNumber && (
                        prUrl
                            ? <a href={prUrl} target="_blank" rel="noopener noreferrer" className={s.headerPr}>PR #{meta.prNumber}</a>
                            : <span className={s.headerPr}>PR #{meta.prNumber}</span>
                    )}
                </div>
                <button className={s.closeBtn} onClick={onClose} aria-label="Close"><DismissRegular /></button>
            </div>

            <div className={s.body}>
                <div className={s.reportPane}>
                    {loading && <div className={s.loadingCenter}>Loading heal report…</div>}
                    {error && <div className={s.errorCenter}>{error}</div>}

                    {report && (
                        <div className={s.reportContent}>
                            <>
                                {/* Summary banner — disposition + counts */}
                                <div className={s.summaryBanner}>
                                    {report.run && (
                                        <div className={s.scopeLine}>
                                            <span>{report.run.scoped ? "Scoped to the change" : "Full suite"}</span>
                                            <span aria-hidden="true">·</span>
                                            <span><span style={{ fontWeight: 600, color: "#c9d1d9" }}>{report.run.tests}</span> tests across <span style={{ fontWeight: 600, color: "#c9d1d9" }}>{report.run.specFiles}</span> spec{report.run.specFiles !== 1 ? "s" : ""}</span>
                                            <span aria-hidden="true">·</span>
                                            <span><span style={{ fontWeight: 600, color: "#3fb950" }}>{report.run.passed}</span> passed</span>
                                            <span aria-hidden="true">·</span>
                                            <span><span style={{ fontWeight: 600, color: "#f85149" }}>{report.run.failed}</span> failed</span>
                                        </div>
                                    )}
                                    {(() => {
                                        const stats = [
                                            { count: appDefects.length, label: "App defects", color: "#f85149" },
                                            { count: other.length, label: "Needs triage", color: "#d29922" },
                                            { count: fixed.length, label: "Fixed", color: "#3fb950" },
                                            { count: stabilized.length, label: "Stabilized", color: "#3fb950" },
                                            { count: flaky.length, label: "Flaky", color: "#d29922" },
                                            { count: removed.length, label: "Retired", color: "#8b949e" },
                                        ].filter((st) => st.count > 0);
                                        return stats.length > 0 ? (
                                            <div className={s.summaryStats}>
                                                {stats.map((st, i) => (
                                                    <React.Fragment key={st.label}>
                                                        {i > 0 && <span className={s.summaryStatDivider} aria-hidden="true" />}
                                                        <SummaryStat count={st.count} label={st.label} color={st.color} />
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : null;
                                    })()}
                                    <div className={s.summaryLabel}>Session summary</div>
                                    <div className={s.summaryText}>{report.summary.text}</div>
                                </div>

                                {/* App defects — needs developer action, shown first */}
                                {appDefects.length > 0 && (
                                    <Section title={`App defects (${appDefects.length})`} color="#f85149" defaultOpen>
                                        {appDefectGroups.map((group, gi) => (
                                            <AppDefectGroup
                                                key={gi}
                                                action={group.action}
                                                items={group.items}
                                                resolveBefore={(cl) => resolveBeforeAfter(cl).before}
                                                gitRemoteUrl={meta?.gitRemoteUrl}
                                                commitSha={meta?.commitSha}
                                                repoTestRoot={meta?.repoTestRoot}
                                                onWatch={(uri, label) => play(uri, label)}
                                            />
                                        ))}
                                    </Section>
                                )}

                                {/* Unclassified — needs triage, shown before completed work */}
                                {other.length > 0 && (
                                    <Section title={`Needs triage (${other.length})`} color="#d29922" defaultOpen>
                                        <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 12, lineHeight: 1.5 }}>
                                            These tests failed but the cause is ambiguous — the app behavior may have changed intentionally, or there may be a bug. Developer review is needed to determine whether to update the test or fix the app.
                                        </div>
                                        {other.map((cl, i) => {
                                            const rec = resolveBeforeAfter(cl).before;
                                            const why = whatWentWrong(cl);
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, cl.file));
                                            return (
                                                <TestCard
                                                    key={i}
                                                    cl={cl}
                                                    fileUrl={fileUrl}
                                                    borderColor="#8b949e"
                                                    status="unclassified"
                                                    actions={rec?.url ? <WatchBtn label="Watch test" onClick={(e) => { e.stopPropagation(); playRecording(rec, testTitle(cl.titlePath)); }} /> : null}
                                                >
                                                    {why && <InfoRow label="What happened" text={why} />}
                                                    {cl.developer_action && <InfoRow label="Developer action" text={cl.developer_action} />}
                                                </TestCard>
                                            );
                                        })}
                                    </Section>
                                )}

                                {/* Fixed */}
                                {fixed.length > 0 && (
                                    <Section title={`Fixed (${fixed.length})`} color="#3fb950" defaultOpen>
                                        {fixed.map((cl, i) => {
                                            const { before: beforeRec, after: afterRec } = resolveBeforeAfter(cl);
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, cl.file));
                                            return (
                                                <TestCard
                                                    key={i}
                                                    cl={cl}
                                                    fileUrl={fileUrl}
                                                    borderColor="#3fb950"
                                                    status="fixed"
                                                    actions={
                                                        <>
                                                            {beforeRec?.url && (
                                                                <WatchBtn label="Before fix" onClick={(e) => { e.stopPropagation(); playRecording(beforeRec, `Before fix — ${testTitle(cl.titlePath)}`); }} />
                                                            )}
                                                            {afterRec?.url && (
                                                                <WatchBtn label="After fix" onClick={(e) => { e.stopPropagation(); playRecording(afterRec, `After fix — ${testTitle(cl.titlePath)}`); }} />
                                                            )}
                                                        </>
                                                    }
                                                >
                                                    {cl.fix_applied && (
                                                        <InfoRow label="Fix Applied" text={cl.fix_applied} />
                                                    )}
                                                    {whatWentWrong(cl) && !cl.fix_applied && (
                                                        <InfoRow label="What changed" text={whatWentWrong(cl)!} />
                                                    )}
                                                </TestCard>
                                            );
                                        })}
                                    </Section>
                                )}

                                {/* Stabilized (deflaked flaky) */}
                                {stabilized.length > 0 && (
                                    <Section title={`Stabilized (${stabilized.length})`} color="#3fb950" defaultOpen>
                                        <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 12, lineHeight: 1.5 }}>
                                            These tests were flaky (non-deterministic), not failing outright. ATA deflaked them so they're reliable — their flow and assertions are unchanged.
                                        </div>
                                        {stabilized.map((cl, i) => {
                                            const { before: beforeRec, after: afterRec } = resolveBeforeAfter(cl);
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, cl.file));
                                            return (
                                                <TestCard
                                                    key={i}
                                                    cl={cl}
                                                    fileUrl={fileUrl}
                                                    borderColor="#3fb950"
                                                    status="fixed"
                                                    actions={
                                                        <>
                                                            {beforeRec?.url && (
                                                                <WatchBtn label="Before fix" onClick={(e) => { e.stopPropagation(); playRecording(beforeRec, `Before fix — ${testTitle(cl.titlePath)}`); }} />
                                                            )}
                                                            {afterRec?.url && (
                                                                <WatchBtn label="After fix" onClick={(e) => { e.stopPropagation(); playRecording(afterRec, `After fix — ${testTitle(cl.titlePath)}`); }} />
                                                            )}
                                                        </>
                                                    }
                                                >
                                                    {cl.fix_applied && <InfoRow label="Fix Applied" text={cl.fix_applied} />}
                                                </TestCard>
                                            );
                                        })}
                                    </Section>
                                )}

                                {/* Flaky */}
                                {flaky.length > 0 && (
                                    <Section title={`Flaky (${flaky.length})`} color="#d29922" defaultOpen>
                                        {flaky.map((cl, i) => {
                                            const rec = resolveBeforeAfter(cl).before;
                                            const why = whatWentWrong(cl);
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, cl.file));
                                            return (
                                                <TestCard
                                                    key={i}
                                                    cl={cl}
                                                    fileUrl={fileUrl}
                                                    borderColor="#d29922"
                                                    status="flaky"
                                                    actions={rec?.url ? <WatchBtn label="Watch test" onClick={(e) => { e.stopPropagation(); playRecording(rec, testTitle(cl.titlePath)); }} /> : null}
                                                >
                                                    {why && <InfoRow label="What happened" text={why} />}
                                                </TestCard>
                                            );
                                        })}
                                    </Section>
                                )}

                                {/* Retired — obsolete tests removed; a completed maintenance action, not a live failure. */}
                                {removed.length > 0 && (
                                    <Section title={`Retired (${removed.length})`} color="#8b949e" defaultOpen>
                                        <div style={{ fontSize: 12, color: "#8b949e", marginBottom: 12, lineHeight: 1.5 }}>
                                            Obsolete tests removed because the behaviour they covered no longer exists.
                                        </div>
                                        {removed.map((cl, i) => {
                                            const why = whatWentWrong(cl) || cl.fix_applied || undefined;
                                            const fileUrl = buildFileUrl(meta?.gitRemoteUrl, meta?.commitSha, repoFilePath(meta?.repoTestRoot, cl.file));
                                            return (
                                                <TestCard
                                                    key={i}
                                                    cl={cl}
                                                    fileUrl={fileUrl}
                                                    borderColor="#8b949e"
                                                    status="removed"
                                                >
                                                    {why && <InfoRow label="Why retired" text={why} status="removed" />}
                                                </TestCard>
                                            );
                                        })}
                                    </Section>
                                )}

                            </>
                        </div>
                    )}
                </div>
            </div>

            {videoModal}
        </div>
    );
};

/* ── App defect group ── */

interface AppDefectGroupProps {
    action: string;
    items: HealClassification[];
    resolveBefore: (cl: HealClassification) => ProjectVideoFile | undefined;
    gitRemoteUrl?: string | null;
    commitSha?: string | null;
    repoTestRoot?: string | null;
    onWatch: (uri: string, label: string) => void;
}

const AppDefectGroup: React.FC<AppDefectGroupProps> = ({ action, items, resolveBefore, gitRemoteUrl, commitSha, repoTestRoot, onWatch }) => {
    const s = useHealReportViewStyles();
    const [expanded, setExpanded] = React.useState(false);
    const head = items[0];
    const why = whatWentWrong(head);
    const title = why ?? testTitle(head.titlePath);
    const fileUrl = buildFileUrl(gitRemoteUrl, commitSha, repoFilePath(repoTestRoot, head.file));
    const displayFile = head.file;
    const reproLabel = items.length === 1 ? "Reproduced by 1 test" : `Reproduced by ${items.length} tests`;

    return (
        <div className={s.testCard} style={{ borderLeftColor: "#f85149" }}>
            <div className={s.testCardTopRow}>
                <div className={s.testCardLeft}>
                    <div className={s.testCardContent}>
                        <div className={s.testCardName} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span className={s.testCardStatusIcon} style={{ color: "#f85149" }}>
                                <DismissCircleFilled />
                            </span>
                            <span>{title}</span>
                            {head.origin && <AttributionTag origin={head.origin} title={originLabel(head.origin) ?? undefined} />}
                        </div>
                        <div className={s.testCardMeta} style={{ paddingLeft: 24 }}>
                            {fileUrl
                                ? <a href={fileUrl} target="_blank" rel="noopener noreferrer" className={s.testCardMetaPath} title={displayFile} style={{ textDecoration: "none" }}>{displayFileName(displayFile)}</a>
                                : <span className={s.testCardMetaPath} title={displayFile}>{displayFileName(displayFile)}</span>}
                        </div>
                    </div>
                </div>
            </div>

            <InfoRow label="Fix needed" text={action} status="appbug" />

            <div className={s.reproducedRow} onClick={() => setExpanded((e) => !e)}>
                {expanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                <span>{reproLabel}</span>
            </div>

            {expanded && (
                <div style={{ borderTop: "1px solid #21262d", paddingTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                    {items.map((cl, i) => {
                        const rec = resolveBefore(cl);
                        const itemFileUrl = buildFileUrl(gitRemoteUrl, commitSha, repoFilePath(repoTestRoot, cl.file));
                        const itemFile = cl.file;
                        return (
                            <div
                                key={i}
                                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: i < items.length - 1 ? "1px solid #21262d" : "none", gap: 12 }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: "#e6edf3", marginBottom: 1 }}>{testTitle(cl.titlePath)}</div>
                                    <div style={{ fontSize: 11, color: "#8b949e" }}>
                                        {itemFileUrl
                                            ? <a href={itemFileUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#388bfd", textDecoration: "none" }} title={itemFile}>{displayFileName(itemFile)}</a>
                                            : <span title={itemFile}>{displayFileName(itemFile)}</span>}
                                    </div>
                                </div>
                                {rec?.url && (
                                    <button
                                        onClick={() => onWatch(rec.url!, testTitle(cl.titlePath))}
                                        className={s.actionBtn}
                                    >
                                        <PlayCircleRegular /> Watch test
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ── Test card ── */

const TestCard: React.FC<{
    cl: HealClassification;
    fileUrl: string | null;
    borderColor?: string;
    status?: "fixed" | "appbug" | "flaky" | "unclassified" | "removed";
    actions?: React.ReactNode;
    children?: React.ReactNode;
}> = ({ cl, fileUrl, borderColor = "#3fb950", status = "fixed", actions, children }) => {
    const s = useHealReportViewStyles();
    const displayFile = cl.file;
    return (
        <div className={s.testCard} style={{ borderLeftColor: borderColor }}>
            <div className={s.testCardTopRow}>
                <div className={s.testCardLeft}>
                    <div className={s.testCardContent}>
                        <div className={s.testCardName} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <StatusIcon status={status} color={borderColor} />
                            <span>{testTitle(cl.titlePath)}</span>
                            {cl.origin && <AttributionTag origin={cl.origin} title={originLabel(cl.origin) ?? undefined} />}
                        </div>
                        <div className={s.testCardMeta} style={{ paddingLeft: 24 }}>
                            {fileUrl
                                ? <a href={fileUrl} target="_blank" rel="noopener noreferrer" className={s.testCardMetaPath} title={displayFile}>{displayFileName(displayFile)}</a>
                                : <span className={s.testCardMetaPath} title={displayFile}>{displayFileName(displayFile)}</span>}
                        </div>
                    </div>
                </div>
                {actions && <div className={s.testCardRight}>{actions}</div>}
            </div>
            {children && <div className={s.testCardBody}>{
                React.Children.map(children, (child) => {
                    if (React.isValidElement(child) && child.type === InfoRow) {
                        return React.cloneElement(child as React.ReactElement<React.ComponentProps<typeof InfoRow>>, { status });
                    }
                    return child;
                })
            }</div>}
        </div>
    );
};

/* ── Watch button ── */

const WatchBtn: React.FC<{ label: string; onClick: (e: React.MouseEvent) => void }> = ({ label, onClick }) => {
    const s = useHealReportViewStyles();
    return (
        <button onClick={onClick} className={s.actionBtn}>
            <PlayCircleRegular style={{ fontSize: 14 }} />
            {label}
        </button>
    );
};

export default HealReportView;
